<template>
  <div class="property-form-container py-4">
    <div class="form-card shadow-sm rounded-4 p-4 bg-white mx-auto">
      <h1 class="h5 fw-semibold mb-4">
        {{ isEdit ? 'Editar Imóvel' : 'Novo Imóvel' }}
      </h1>

      <div v-if="loading" class="py-5 text-muted">Carregando...</div>
      <div v-else>
        <div v-if="persistentError" class="alert alert-danger d-flex justify-content-between align-items-start">
          <div>{{ persistentError }}</div>
          <button type="button" class="btn-close" aria-label="Fechar" @click="closePersistentError"></button>
        </div>
        <div v-else-if="error" class="alert alert-danger">{{ error }}</div>

        <form @submit.prevent="submit" enctype="multipart/form-data" novalidate>
          <div class="row g-3">
            <!-- Seção: Informações Principais -->
            <div class="col-md-12">
              <div class="section-card">
                <div class="row g-3">
                  <div class="col-md-8">
                    <label class="form-label fw-semibold">Título do Imóvel</label>
                    <input class="form-control form-control-lg" v-model="form.title" required />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label fw-semibold">Preço (R$)</label>
                    <input type="number" class="form-control form-control-lg" v-model.number="form.price" required />
                  </div>
                  <div class="col-md-12">
                    <label class="form-label">Descrição</label>
                    <textarea class="form-control" rows="3" v-model="form.description" placeholder="Descreva os destaques do imóvel..."></textarea>
                  </div>
                </div>
              </div>
            </div>

            <!-- Seção: Endereço (com melhor layout) -->
            <div class="col-md-12">
              <div class="section-card">
                <h6 class="section-title">Endereço</h6>
                <div class="row g-3">
                  <div class="col-md-2">
                    <label class="form-label">CEP</label>
                    <div class="position-relative">
                      <input 
                        class="form-control" 
                        v-model="form.zip_code" 
                        @blur="fetchAddressFromCep"
                        @input="handleCepInput"
                        maxlength="9"
                        placeholder="00000-000"
                      />
                      <span v-if="cepLoading" class="position-absolute end-0 top-50 translate-middle-y me-2">
                        <span class="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true"></span>
                      </span>
                    </div>
                    <div v-if="cepError" class="form-text text-danger small">{{ cepError }}</div>
                  </div>
                  <div class="col-md-8">
                    <label class="form-label">Endereço (Rua/Avenida)</label>
                    <input class="form-control" v-model="form.address" required />
                  </div>
                  <div class="col-md-2">
                    <label class="form-label">Número</label>
                    <input class="form-control" v-model="form.number" placeholder="123" />
                  </div>
                  <div class="col-md-5">
                    <label class="form-label">Bairro</label>
                    <input class="form-control" v-model="form.neighborhood" />
                  </div>
                  <div class="col-md-5">
                    <label class="form-label">Cidade</label>
                    <input class="form-control" v-model="form.city" required />
                  </div>
                  <div class="col-md-2">
                    <label class="form-label">Estado</label>
                    <input class="form-control text-uppercase" maxlength="2" v-model="form.state" required />
                  </div>
                </div>
              </div>
            </div>

            <!-- Seção: Características -->
            <div class="col-md-12">
              <div class="section-card">
                <h6 class="section-title">Características</h6>
                <div class="row g-3">
                  <div class="col-md-4">
                    <label class="form-label">Tipo de Imóvel</label>
                    <select class="form-select" v-model="form.property_type" required>
                      <option disabled value="">Selecione...</option>
                      <option value="house">Casa</option>
                      <option value="apartment">Apartamento</option>
                      <option value="land">Terreno</option>
                      <option value="commercial">Comercial</option>
                    </select>
                  </div>
                  <div class="col-md-2">
                    <label class="form-label">Área (m²)</label>
                    <input type="number" step="0.01" class="form-control" v-model.number="form.area" />
                  </div>
                  <div class="col-md-2">
                    <label class="form-label">Quartos</label>
                    <input type="number" class="form-control" v-model.number="form.bedrooms" min="0" />
                  </div>
                  <div class="col-md-2">
                    <label class="form-label">Banheiros</label>
                    <input type="number" class="form-control" v-model.number="form.bathrooms" min="0" />
                  </div>
                  <div class="col-md-2">
                    <label class="form-label">Vagas</label>
                    <input type="number" class="form-control" v-model.number="form.parking_spaces" min="0" />
                  </div>
                  <div class="col-md-2">
                    <label class="form-label">Condomínio (R$)</label>
                    <input type="number" step="0.01" min="0" class="form-control" v-model.number="form.condominium_fee" />
                  </div>
                  <div class="col-md-2">
                    <label class="form-label">IPTU (R$)</label>
                    <input type="number" step="0.01" min="0" class="form-control" v-model.number="form.iptu" />
                  </div>
                  <div class="col-md-2">
                    <label class="form-label">Ano construção</label>
                    <input type="number" class="form-control" v-model.number="form.year_built" placeholder="2020" />
                  </div>
                  <div class="col-md-2">
                    <label class="form-label">Mobiliado</label>
                    <select class="form-select" v-model="form.furnished">
                      <option :value="null">Selecione</option>
                      <option :value="true">Sim</option>
                      <option :value="false">Não</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <!-- Seção: Status -->
            <div class="col-md-12">
              <div class="section-card">
                <h6 class="section-title">Status</h6>
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label">Status do Imóvel</label>
                    <select class="form-select" v-model="form.status">
                      <option value="available">Disponível</option>
                      <option value="under_offer">Em Negociação</option>
                      <option value="sold">Vendido</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="canEditAgent" class="col-md-12">
              <div class="section-card">
                <h6 class="section-title">Pessoas relacionadas</h6>
                <div class="row g-3">
                  <div class="col-md-4">
                    <label class="form-label">Corretor Responsável</label>
                    <select class="form-select" v-model.number="form.agent_id">
                      <option :value="null">Selecione um corretor...</option>
                      <option v-for="a in agents" :key="a.id" :value="a.id">
                        {{ a.name || a.email }}
                      </option>
                    </select>
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Proprietário</label>
                    <select class="form-select" v-model.number="form.owner_id">
                      <option :value="null">Nenhum selecionado</option>
                      <option v-for="p in people" :key="p.id" :value="p.id">
                        {{ p.name }}
                      </option>
                    </select>
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Inquilino</label>
                    <select class="form-select" v-model.number="form.tenant_id">
                      <option :value="null">Nenhum selecionado</option>
                      <option v-for="p in people" :key="p.id" :value="p.id">
                        {{ p.name }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <!-- Seção: Fotos -->
            <div class="col-12">
              <div class="section-card">
                <h6 class="section-title">Galeria de Fotos</h6>
                <label class="form-label">Selecione as fotos do imóvel</label>
                <input ref="fileInput" type="file" multiple class="form-control" @change="onFilesChange" />

              <div v-if="previewUrls.length" class="preview-container mt-3 d-flex flex-wrap gap-3">
                <div
                  v-for="(u,i) in previewUrls"
                  :key="'new-'+i"
                  class="preview-box position-relative shadow-sm rounded"
                >
                  <img :src="u" class="preview-img rounded-3" />
                  <button
                    type="button"
                    class="btn btn-sm btn-danger position-absolute"
                    style="right:-8px;top:-8px;border-radius:50%;"
                    @click="removeNewFile(i)"
                  >&times;</button>
                </div>
              </div>

              <div v-if="existingPhotos.length" class="mt-3">
                <p class="small text-muted mb-2">Fotos salvas:</p>
                <div class="d-flex flex-wrap gap-3">
                  <div
                    v-for="(p, idx) in existingPhotos"
                    :key="p.blob_id"
                    class="preview-box position-relative shadow-sm rounded"
                  >
                    <img :src="p.url" :alt="p.filename" class="preview-img rounded-3" />
                    <button
                      type="button"
                      class="btn btn-sm btn-danger position-absolute"
                      style="right:-8px;top:-8px;border-radius:50%;"
                      @click="markRemoveExisting(p.blob_id)"
                    >&times;</button>
                  </div>
                </div>
                <div v-if="removedBlobIds.length" class="text-muted small mt-2">
                  Marcou {{ removedBlobIds.length }} foto(s) para remoção
                </div>
              </div>
            </div>
            </div>
          </div>

          <div class="mt-4">
            <button
              class="btn btn-primary px-5 py-2 fw-semibold shadow-sm"
              type="submit"
              :disabled="submitting"
            >
              <span v-if="submitting" class="d-inline-flex align-items-center">
                <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Salvando...
              </span>
              <span v-else>
                {{ isEdit ? 'Salvar Alterações' : 'Criar Imóvel' }}
              </span>
            </button>
            <button
              v-if="isEdit"
              class="btn btn-outline-danger ms-2 px-4 py-2"
              type="button"
              :disabled="deleting || submitting"
              @click="removeProperty"
            >
              <span v-if="deleting" class="d-inline-flex align-items-center">
                <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Excluindo...
              </span>
              <span v-else>Excluir Imóvel</span>
            </button>
            <router-link to="/" class="btn btn-outline-secondary ms-2 px-4 py-2">Cancelar</router-link>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()
const id = route.params.id
const isEdit = Boolean(id)

const form = ref({
  title: '',
  price: 0,
  property_type: '',     // required
  address: '',           // required
  number: '',
  neighborhood: '',
  zip_code: '',
  city: '',
  state: '',
  area: null,
  bedrooms: 0,
  bathrooms: 0,
  parking_spaces: 0,
  furnished: false,
  condominium_fee: 0.0,
  iptu: 0.0,
  year_built: null,
  status: 'available',
  description: '',
  agent_id: null,
  owner_id: null,
  tenant_id: null
})

// controle de quem é o corretor responsável (visível somente para agentes/admins)
const currentUser = ref(null)
const canEditAgent = ref(false)
const agents = ref([])
const people = ref([])
form.value.agent_id = null

const loading = ref(false)
const error = ref(null)
// novo ref persistente para a mensagem de validação que não deve sumir automaticamente
const persistentError = ref(null)

// novo estado para submissão
const submitting = ref(false)
const deleting = ref(false)
const success = ref(null)

// estados para busca de CEP
const cepLoading = ref(false)
const cepError = ref(null)

const files = ref([])
const previewUrls = ref([])

// fotos já atreladas e removidas
const existingPhotos = ref([]) // objetos { blob_id, filename, url }
const removedBlobIds = ref([])

const fileInput = ref(null)

const getCsrf = () => {
  try {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
  } catch {
    return ''
  }
}

const load = async () => {
  if (!isEdit) return
  loading.value = true
  try {
    const res = await fetch(`/properties/${id}.json`, { credentials: 'same-origin' })
    if (!res.ok) throw new Error('Imóvel não encontrado')
    const data = await res.json()
    Object.assign(form.value, {
      title: data.title ?? '',
      price: data.price ?? 0,
      property_type: data.property_type ?? '',
      address: data.address ?? '',
      number: data.number ?? '',
      neighborhood: data.neighborhood ?? '',
      zip_code: data.zip_code ?? '',
      city: data.city ?? '',
      state: data.state ?? '',
      area: data.area ?? null,
      bedrooms: data.bedrooms ?? 0,
      bathrooms: data.bathrooms ?? 0,
      parking_spaces: data.parking_spaces ?? 0,
      furnished: data.furnished ?? false,
      condominium_fee: data.condominium_fee ?? 0.0,
      iptu: data.iptu ?? 0.0,
      year_built: data.year_built ?? null,
      status: data.status ?? 'available',
      description: data.description ?? '',
      agent_id: data.agent_id ?? null,
      owner_id: data.owner_id ?? null,
      tenant_id: data.tenant_id ?? null
    })
    existingPhotos.value = data.photos_data || []
  } catch (e) {
    error.value = e.message || String(e)
  } finally {
    loading.value = false
  }
}

onMounted(load)

// carrega usuário atual e lista de agentes para o seletor
onMounted(async () => {
  try {
    const ures = await fetch('/current_user', { credentials: 'same-origin' })
    if (ures.ok) {
      currentUser.value = await ures.json()
      canEditAgent.value = !!currentUser.value && (currentUser.value.role === 'admin' || currentUser.value.role === 'agent')
      if (canEditAgent.value) {
        // regras: se agente => mostrar todos admins + ele mesmo
        // se admin => mostrar todos admins + todos agentes
        const adminRes = await fetch('/users.json?role=2', { credentials: 'same-origin' })
        let admins = []
        if (adminRes.ok) admins = await adminRes.json()

        if (currentUser.value.role === 'agent') {
          agents.value = [ ...admins.filter(a => a.id !== currentUser.value.id), currentUser.value ]
          form.value.agent_id = currentUser.value.id // default para agente
        } else if (currentUser.value.role === 'admin') {
          const agentRes = await fetch('/users.json?role=1', { credentials: 'same-origin' })
          let corr = []
          if (agentRes.ok) corr = await agentRes.json()
          agents.value = [ ...admins, ...corr ]
          form.value.agent_id = form.value.agent_id || currentUser.value.id
        }
        // ordena por nome/email
        agents.value.sort((a,b) => (a.name||a.email||'').localeCompare(b.name||b.email||''))
      }
      
      // carrega lista de pessoas para proprietário e inquilino
      const pRes = await fetch('/people.json', { credentials: 'same-origin' })
      if (pRes.ok) {
        people.value = await pRes.json()
        people.value.sort((a,b) => (a.name||'').localeCompare(b.name||''))
      }
    }
  } catch {}
})

// revoke object URLs on unmount
onBeforeUnmount(() => {
  previewUrls.value.forEach(u => {
    try { URL.revokeObjectURL(u) } catch {}
  })
})

// arquivos selecionados
const onFilesChange = (e) => {
  const incoming = Array.from(e.target.files || [])

  // build a Set of signatures for existing files to avoid duplicates
  const existingSigs = new Set(files.value.map(f => `${f.name}::${f.size}::${f.lastModified}`))

  // filter only truly new files
  const newFiles = incoming.filter(f => {
    const sig = `${f.name}::${f.size}::${f.lastModified}`
    if (existingSigs.has(sig)) return false
    existingSigs.add(sig)
    return true
  })

  if (newFiles.length === 0) {
    // clear input to allow re-selecting same files later
    if (fileInput.value) fileInput.value.value = ''
    return
  }

  // append new files to the reactive files array
  files.value = files.value.concat(newFiles)

  // create and append previews only for the new files
  const newPreviews = newFiles.map(f => URL.createObjectURL(f))
  previewUrls.value = previewUrls.value.concat(newPreviews)

  // clear input so selecting the same files again will fire change
  if (fileInput.value) fileInput.value.value = ''
}

// remove um novo arquivo selecionado antes do envio
const removeNewFile = (index) => {
  if (previewUrls.value[index]) {
    try { URL.revokeObjectURL(previewUrls.value[index]) } catch {}
  }
  files.value.splice(index, 1)
  previewUrls.value.splice(index, 1)
  // clear file input if none left to allow re-upload of same files
  if (fileInput.value && files.value.length === 0) fileInput.value.value = ''
}

// marcar uma foto existente para remoção (toggle)
const markRemoveExisting = (blobId) => {
  const n = Number(blobId)
  if (removedBlobIds.value.includes(n)) {
    removedBlobIds.value = removedBlobIds.value.filter(x => x !== n)
    // opcional: re-add original photo to list (não re-fetch para simplicidade)
  } else {
    removedBlobIds.value.push(n)
    existingPhotos.value = existingPhotos.value.filter(p => Number(p.blob_id) !== n)
  }
}

const submit = async () => {
  error.value = null
  success.value = null

  // validacoes simples
  if (!form.value.title || !form.value.price || !form.value.property_type || !form.value.address || !form.value.city || !form.value.state) {
    persistentError.value = 'Preencha os campos obrigatórios: título, preço, tipo, endereço, cidade e estado.'
    return
  }

  submitting.value = true
  try {
    const method = isEdit ? 'PATCH' : 'POST'
    const url = isEdit ? `/properties/${id}.json` : '/properties.json'

    const hasFiles = files.value.length > 0
    const hasRemovals = removedBlobIds.value.length > 0

    let body, headers = {}
    if (hasFiles || hasRemovals) {
      body = new FormData()
      // append property fields safely converting to strings
      Object.keys(form.value).forEach(k => {
        const v = form.value[k]
        if (v === undefined || v === null) return
        // boolean -> string, numbers -> string
        body.append(`property[${k}]`, (typeof v === 'object') ? JSON.stringify(v) : String(v))
      })
      files.value.forEach(f => body.append('property[uploaded_photos][]', f))
      removedBlobIds.value.forEach(id => body.append('property[remove_photo_blob_ids][]', String(id)))
      headers['X-CSRF-Token'] = getCsrf()
      // do NOT set Content-Type for FormData
    } else {
      body = JSON.stringify({ property: form.value })
      headers['Content-Type'] = 'application/json'
      headers['X-CSRF-Token'] = getCsrf()
    }

    const res = await fetch(url, {
      method,
      headers,
      body,
      credentials: 'same-origin'
    })

    if (res.status === 422) {
      const bodyErr = await res.json().catch(() => null)
      throw new Error((bodyErr && (bodyErr.errors || bodyErr.error)) || 'Erro de validação')
    }
    if (!res.ok) {
      const txt = await res.text().catch(() => '')
      throw new Error(txt || 'Erro ao salvar')
    }
    router.push({ path: '/' })
  } catch (e) {
    error.value = e.message || String(e)
  } finally {
    submitting.value = false
  }
}

const removeProperty = async () => {
  if (!isEdit) return
  if (!confirm('Confirma exclusão do imóvel? Esta ação não pode ser desfeita.')) return

  error.value = null
  deleting.value = true
  try {
    const res = await fetch(`/properties/${id}.json`, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': getCsrf()
      },
      credentials: 'same-origin'
    })

    if (!res.ok) {
      let message = 'Erro ao excluir imóvel'
      const contentType = res.headers.get('content-type') || ''

      if (contentType.includes('application/json')) {
        const bodyErr = await res.json().catch(() => null)
        message = bodyErr?.error || bodyErr?.errors || message
      } else {
        const txt = await res.text().catch(() => '')
        if (txt && !txt.toLowerCase().includes('<!doctype html')) {
          message = txt
        }
      }

      throw new Error(message)
    }

    alert('Imóvel excluído com sucesso.')
    router.push({ path: '/' })
  } catch (e) {
    error.value = e.message || String(e)
  } finally {
    deleting.value = false
  }
}

// função para o botão fechar do alerta persistente
const closePersistentError = () => {
  persistentError.value = null
}

// Formata o CEP enquanto o usuário digita
const handleCepInput = (e) => {
  let value = e.target.value.replace(/\D/g, '') // Remove não-dígitos
  if (value.length > 5) {
    value = value.slice(0, 5) + '-' + value.slice(5, 8)
  }
  form.value.zip_code = value
  cepError.value = null
}

// Busca endereço pela API ViaCEP quando o usuário sai do campo
const fetchAddressFromCep = async () => {
  const cep = form.value.zip_code?.replace(/\D/g, '') // Remove formatação
  
  if (!cep || cep.length !== 8) {
    return // CEP inválido ou incompleto, não faz nada
  }
  
  cepLoading.value = true
  cepError.value = null
  
  try {
    const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
    
    if (!response.ok) {
      throw new Error('Erro ao consultar CEP')
    }
    
    const data = await response.json()
    
    if (data.erro) {
      cepError.value = 'CEP não encontrado'
      return
    }
    
    // Preenche os campos automaticamente
    if (data.logradouro) {
      form.value.address = data.logradouro
    }
    if (data.bairro) {
      form.value.neighborhood = data.bairro
    }
    if (data.localidade) {
      form.value.city = data.localidade
    }
    if (data.uf) {
      form.value.state = data.uf.toUpperCase()
    }
    
  } catch (err) {
    console.error('Erro ao buscar CEP:', err)
    cepError.value = 'Erro ao consultar CEP. Tente novamente.'
  } finally {
    cepLoading.value = false
  }
}
</script>

<style scoped>
.property-form-container {
  background-color: #f9fafc;
  min-height: 100vh;
}

.form-card {
  max-width: 900px;
}

.section-card {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 0.5rem;
  transition: all 0.2s ease;
}

.section-card:hover {
  border-color: #dee2e6;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.section-title {
  color: #495057;
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #f0f0f0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.preview-box {
  width: 110px;
  height: 80px;
  overflow: hidden;
  border: 1px solid #ddd;
  background-color: #f8f9fa;
  position: relative;
}

.preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.form-control:focus,
.form-select:focus {
  border-color: #0d6efd;
  box-shadow: 0 0 0 0.15rem rgba(13, 110, 253, 0.25);
}

.btn-primary {
  background-color: #0d6efd;
  border: none;
}

.btn-primary:hover {
  background-color: #0b5ed7;
}

.btn-outline-secondary:hover {
  background-color: #f1f1f1;
}
</style>
